import { useEffect, useState } from 'react'
import { IStreetAddress, STATE_CODES } from "@prescreen/lib/street-address"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Check, ChevronsUpDown } from "lucide-react"
import { cn } from "@prescreen/lib/utils"
import { VITE_GOOGLE_PLACES_API_KEY } from "@prescreen/lib/config"

// You'll need to install these components first:
// npx shadcn@latest add command popover
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export type AddressProps = {
  label?: string
  address: IStreetAddress
  onChange: (address: IStreetAddress) => void
}

export const AddressInputs = ({ label, address = {}, onChange }: AddressProps) => {
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false)
  const [placesService, setPlacesService] = useState<any>(null)
  const [placePredictions, setPlacePredictions] = useState<any[]>([])
  const [isPlacePredictionsLoading, setIsPlacePredictionsLoading] = useState(false)
  const [line1, setLine1] = useState(address.streetAddress || '')
  const [showSuggestions, setShowSuggestions] = useState(false)

  // Load Google Places API dynamically
  useEffect(() => {
    const loadGooglePlaces = async () => {
      if (typeof window === 'undefined') return

      // Check if Google is already loaded
      if (window.google && window.google.maps && window.google.maps.places) {
        setIsGoogleLoaded(true)
        return
      }

      // Load Google Maps script
      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?key=${VITE_GOOGLE_PLACES_API_KEY}&libraries=places`
      script.async = true
      script.defer = true
      
      script.onload = () => {
        setIsGoogleLoaded(true)
      }
      
      script.onerror = () => {
        console.error('Failed to load Google Places API')
      }

      document.head.appendChild(script)
    }

    loadGooglePlaces()
  }, [])

  // Initialize Places Service when Google is loaded
  useEffect(() => {
    if (isGoogleLoaded && window.google) {
      const service = new window.google.maps.places.PlacesService(
        document.createElement('div')
      )
      setPlacesService(service)
    }
  }, [isGoogleLoaded])

  useEffect(() => {
    setLine1(address.streetAddress || '')
  }, [address.streetAddress])

  const getPlacePredictions = (request: { input: string }) => {
    if (!isGoogleLoaded || !window.google) return

    setIsPlacePredictionsLoading(true)
    
    const autocompleteService = new window.google.maps.places.AutocompleteService()
    autocompleteService.getPlacePredictions(
      {
        input: request.input,
        types: ['address'],
        componentRestrictions: { country: ['us'] }
      },
      (predictions, status) => {
        setIsPlacePredictionsLoading(false)
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setPlacePredictions(predictions)
          setShowSuggestions(true)
        } else {
          setPlacePredictions([])
          setShowSuggestions(false)
        }
      }
    )
  }

  const handlePlaceSelect = (place: any) => {
    if (!placesService) return

    placesService.getDetails({
      placeId: place.place_id,
    }, (placeDetails) => {
      if (!placeDetails) return

      const city = placeDetails.address_components?.find(c => c.types.includes('locality'))
      const state = placeDetails.address_components?.find(c => c.types.includes('administrative_area_level_1'))
      const zip = placeDetails.address_components?.find(c => c.types.includes('postal_code'))
      const country = placeDetails.address_components?.find(c => c.types.includes('country'))

      onChange({
        ...address,
        city: city?.short_name || '',
        state: state?.short_name || '',
        zip: zip?.short_name || '',
        streetAddress: placeDetails.name,
        country: country?.long_name
      })
      setLine1(placeDetails.name)
      setShowSuggestions(false)
    })
  }

  const stateOptions = Object.entries(STATE_CODES).map(([code, name]) => ({
    value: code,
    label: `${code} - ${name}`
  }))

  const countryOptions = [
    { value: 'United States', label: 'United States' },
    { value: 'Canada', label: 'Canada' },
    { value: 'Mexico', label: 'Mexico' }
  ]

  return (
    <div className="space-y-4">
      {/* Address Line 1 with Google Places Autocomplete */}
      <div className="space-y-2">
        <div className="relative">
          <Input
            type="text"
            placeholder="Address Line 1"
            value={line1}
            onChange={(ev) => {
              const value = ev.target.value
              setLine1(value)
              if (value.length > 2) {
                getPlacePredictions({ input: value })
              } else {
                setPlacePredictions([])
                setShowSuggestions(false)
              }
            }}
            onFocus={() => {
              if (placePredictions.length > 0) {
                setShowSuggestions(true)
              }
            }}
            onBlur={() => {
              // Delay hiding suggestions to allow for clicks
              setTimeout(() => setShowSuggestions(false), 200)
            }}
          />
          
          {/* Suggestions Dropdown */}
          {showSuggestions && placePredictions.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
              {isPlacePredictionsLoading && placePredictions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">Searching...</div>
              ) : (
                placePredictions.map((place) => (
                  <div
                    key={place.place_id}
                    className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100"
                    onClick={() => handlePlaceSelect(place)}
                  >
                    {place.description}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Address Line 2 */}
      <div className="space-y-2">
        <Input
          type="text"
          placeholder="Address Line 2 (optional)"
          value={address?.streetAddressLine2 || ''}
          onChange={ev => onChange({
            ...address,
            streetAddressLine2: ev.target.value
          })}
        />
      </div>

      {/* City, State, Zip, Country Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* City */}
        <div className="space-y-2">
          <Label>City</Label>
          <Input
            type="text"
            placeholder="City"
            value={address?.city || ''}
            onChange={ev => onChange({
              ...address,
              city: ev.target.value
            })}
          />
        </div>

        {/* State */}
        <div className="space-y-2">
          <Label>State</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                className="w-full justify-between"
              >
                {address?.state ? stateOptions.find(s => s.value === address.state)?.label : "Choose state..."}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0">
              <Command>
                <CommandInput placeholder="Search states..." />
                <CommandList>
                  <CommandEmpty>No state found.</CommandEmpty>
                  <CommandGroup>
                    {stateOptions.map((state) => (
                      <CommandItem
                        key={state.value}
                        value={state.value}
                        onSelect={() => onChange({
                          ...address,
                          state: state.value
                        })}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            address?.state === state.value ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {state.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* ZIP */}
        <div className="space-y-2">
          <Label>ZIP</Label>
          <Input
            placeholder="ZIP/Postal Code"
            value={address?.zip || ''}
            onChange={ev => onChange({
              ...address,
              zip: ev.target.value.replace(/[^0-9]/gi, '').substring(0, 5)
            })}
          />
        </div>

        {/* Country */}
        <div className="space-y-2">
          <Label>Country</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                className="w-full justify-between"
              >
                {address?.country || "Select Country"}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0">
              <Command>
                <CommandInput placeholder="Search countries..." />
                <CommandList>
                  <CommandEmpty>No country found.</CommandEmpty>
                  <CommandGroup>
                    {countryOptions.map((country) => (
                      <CommandItem
                        key={country.value}
                        value={country.value}
                        onSelect={() => onChange({
                          ...address,
                          country: country.value
                        })}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            address?.country === country.value ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {country.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  )
}